// AIzaSyAkg4C6quqYmK2JPFooVTLw8NLwfYnQvc0

import React, {
  useCallback,
  useState,
  useRef,
  useEffect
} from "react";
import {
  GoogleMap,
  useJsApiLoader,
  Polyline,
  OverlayView,
} from "@react-google-maps/api";
// (Install the new Google-supplied marker clusterer if you wish to cluster)
import { MarkerClusterer } from "@googlemaps/markerclusterer";
import CameraAltOutlinedIcon from "@mui/icons-material/CameraAltOutlined";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import RefreshIcon from "@mui/icons-material/Refresh";
import CancelIcon from "@mui/icons-material/Cancel";
import Fab from "@mui/material/Fab";
import Tooltip from "@mui/material/Tooltip";
import CircularProgress from "@mui/material/CircularProgress";
import Switch from "@mui/material/Switch";
import FormControlLabel from "@mui/material/FormControlLabel";
import Box from "@mui/material/Box";

// Example icons for lines or default usage
import hydrantSVG from "../markerIcons/hydrant.svg";
import manholeSVG from "../markerIcons/manhole.svg";
import trafficSignSVG from "../markerIcons/trafficSign.svg";
import valveSVG from "../markerIcons/valve.svg";
import waterMeterSVG from "../markerIcons/watermeter.svg";
import searchRemoveHighlight from "../markerIcons/search.png";

// Utility for colored hydrant icons
import { getColoredHydrantSvg } from "../utils/coloredHydrantIcon";

// Utility for line color coding by type and diameter
import { getLineColor, getArrowColorByDiameter } from "../utils/lineColorUtils";

// Floating legend for line colors
import LineLegend from "./LineLegend";

// Reverse geocode utility for auto-filling address fields
import { reverseGeocode } from "../utils/geocodeUtils";

/**
 * Container style. You could also just do width/height: "100%" if your parent is sized.
 */


function CustomMap({
  zoom,
  markers,
  lines,
  addingMarker,
  mapCenter,
  showFormAndAddMarker,
  addedAssetType,
  movingMarker,
  editingData,
  CallBackToParent,
  cancelMarker,
  EditMarker,
  moveMarker,
  resetMarker,
  markerLatLng,
  addedLinesLatLng,
  CallBackToParentForLine,
  deleteMarker,
  closeAllPopups,
  isLoadingLocation,
  addingPolyLine,
  clearLines,
  clearLinesFunc,
  // Permission props
  canWrite = true,
  isReadOnly = false,
  // Live preview for marker color changes
  previewMarkerUpdate = null,
  // Reverse geocode callback for auto-filling address fields
  onGeocodedAddress,
  // QuickCapture handler for camera button
  onQuickCaptureClick,
  // Location and refresh button handlers
  onCenterMap,
  onRefreshData,
  refreshing = false,
  hasHighlightedMarkers = false,
  onRemoveHighlights,
  // ...other props from your code that handle line editing, etc.
}) {
  // 1) Load the Maps JS
  const { isLoaded } = useJsApiLoader({
    id: "google-map-script",
    // Replace with your own API key
    googleMapsApiKey: "AIzaSyAkg4C6quqYmK2JPFooVTLw8NLwfYnQvc0"
  });
  // 2) Set the map center and zoom level

  // 2) We'll store the map instance in state
  const [map, setMap] = useState(null);
  const [isEditingMarker, setIsEditingMarker] = useState(false);
  const [initialAdd, setInitialAdd] = useState(false);
  const [lineCoordinates, setLineCoordinates] = useState([]);
  const [selectedMarkerId, setSelectedMarkerId] = useState(null);
  const [selectedLineId, setSelectedLineId] = useState(null);
  const [googleMapCenter, setGoogleMapCenter] = useState(null);
  const [currentMapType, setCurrentMapType] = useState("satellite"); // Track current map type

  const isMobileDevice = () => {
    return /Mobi|Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
  }

  const [isMobile, setIsMobile] = useState(isMobileDevice());
  // account for Navbar height
  const navbarHeight = window.innerWidth < 502 ? isMobile ? 68 : 73.6 : 73.6; // Adjust this value based on your navbar height
  const relativNavbarHeight = (navbarHeight / window.innerHeight) * 100; // Convert to percentage
  const [containerStyle, setContainerStyle] = useState({
    position: "fixed",
    marginTop: `${relativNavbarHeight}vh`,
    width: "100vw",
    height: `${100 - relativNavbarHeight}vh`,
  });
  // 3) Imperative marker references
  const markersRef = useRef([]);
  const clustererRef = useRef(null);
  
  // Use ref to store current addingPolyLine value for marker click handlers
  const addingPolyLineRef = useRef(addingPolyLine);
  
  // Use refs to store current lineCoordinates and addedLinesLatLng values to prevent stale closures
  const lineCoordinatesRef = useRef(lineCoordinates);
  const addedLinesLatLngRef = useRef(addedLinesLatLng);
  
  // Use ref for canWrite permission to use in callbacks
  const canWriteRef = useRef(canWrite);
  
  // Update refs whenever props change
  useEffect(() => {
    addingPolyLineRef.current = addingPolyLine;
    console.log("addingPolyLineRef updated to:", addingPolyLine);
  }, [addingPolyLine]);
  
  // Update canWrite ref when prop changes
  useEffect(() => {
    canWriteRef.current = canWrite;
  }, [canWrite]);
  
  useEffect(() => {
    lineCoordinatesRef.current = lineCoordinates;
  }, [lineCoordinates]);
  
  useEffect(() => {
    addedLinesLatLngRef.current = addedLinesLatLng;
  }, [addedLinesLatLng]);

  // On map load
  const onLoad = useCallback((mapInstance) => {
    setMap(mapInstance);
    // Optionally center the map if we have mapCenter
    if (mapCenter && mapCenter.length === 2) {
      const center = toLatLngLiteral(mapCenter[0], mapCenter[1]);
      if (center) mapInstance.setCenter(center);
      setGoogleMapCenter(mapCenter);
    }
    mapInstance.setZoom(zoom || 12);

    // Create the clusterer (if you want to cluster) – otherwise you can skip
    clustererRef.current = new MarkerClusterer({
      map: mapInstance,
      markers: [],  // We'll add them after we build them
      // Additional clusterer options
      // e.g. minimumClusterSize: 5
    });
  }, [mapCenter, zoom]);

  // On unmount
  const onUnmount = useCallback(() => {
    // Clear everything
    // 1) remove all markers from the map
    markersRef.current.forEach(marker => marker.setMap(null));
    markersRef.current = [];
    // 2) remove clusterer
    if (clustererRef.current) {
      clustererRef.current.clearMarkers();
      clustererRef.current = null;
    }

    setMap(null);
  }, []);

  // 4) Resize the map on window resize (optional)
  useEffect(() => {

    const handleResize = () => {
      console.log("Screen Size", window.innerHeight)
      setIsMobile(isMobileDevice());
      // Convert to percentage
      if (window.innerWidth < 767 && showFormAndAddMarker) {
        const relativNavbarHeight = ((isMobile ? 68 : 73.6) / window.innerHeight) * 100;
        setContainerStyle({
          position: "fixed",
          marginTop: `${relativNavbarHeight}vh`,
          width: isMobile ? "100vw" : "97vw",
          height: isMobile ? `${45 - relativNavbarHeight}vh` : `${50 - relativNavbarHeight}vh`,
        });
      } else {
        let mobileNavBarheight = window.innerWidth < 502 ? (isMobile ? 68 : 73.6) : navbarHeight;
        const relativNavbarHeight = (mobileNavBarheight / window.innerHeight) * 100; // Convert to percentage
        setContainerStyle({
          position: "fixed",
          marginTop: `${relativNavbarHeight}vh`,
          width: "100vw",
          height: `${100 - relativNavbarHeight}vh`,
        });
      }
    };
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    }
  }, []);

  // Helper function to create readable tooltips
  const createReadableTooltip = (markerData) => {
    if (!markerData) return "Marker";
    
    const assetType = markerData.assetType || "Asset";
    
    // Try to find a meaningful ID for this asset type
    let assetId = null;
    const possibleIdFields = [
      `Client${assetType}ID`,
      `${assetType}ID`,
      'AssetID',
      'ID'
    ];
    
    for (const field of possibleIdFields) {
      if (markerData[field]) {
        assetId = markerData[field];
        break;
      }
    }
    
    // Format the asset type for display (handle special cases)
    let displayType = assetType;
    switch (assetType) {
      case 'WaterMeter':
        displayType = 'Water Meter';
        break;
      case 'TrafficSign':
        displayType = 'Traffic Sign';
        break;
      case 'WaterLine':
        displayType = 'Water Line';
        break;
      case 'SewerLine':
        displayType = 'Sewer Line';
        break;
      default:
        displayType = assetType;
    }
    
    // Create the tooltip text
    if (assetId) {
      return `${displayType} #${assetId}`;
    } else {
      return displayType;
    }
  };

  const toFiniteNumber = (value) => {
    const n = typeof value === "number" ? value : Number.parseFloat(value);
    return Number.isFinite(n) ? n : null;
  };

  const toLatLngLiteral = (latLike, lngLike) => {
    const lat = toFiniteNumber(latLike);
    const lng = toFiniteNumber(lngLike);
    if (lat === null || lng === null) return null;
    // Optional sanity bounds (avoid weird DB garbage crashing/zooming map)
    if (lat < -90 || lat > 90 || lng < -180 || lng > 180) return null;
    return { lat, lng };
  };

  // Create or update markers whenever `markers` prop changes
  useEffect(() => {
    if (!map) return;

    // Check if only the newMarker position has changed
    const existingNewMarker = markersRef.current.find(m => m.title === "newMarker");
    const newMarkerInData = markers && markers.find(m => m && m.GlobalID === "newMarker");
    
    // If we just need to update the newMarker position
    if (existingNewMarker && newMarkerInData) {
      const newPosition = toLatLngLiteral(newMarkerInData.Latitude, newMarkerInData.Longitude);
      if (newPosition) {
        existingNewMarker.setPosition(newPosition);
      }
      existingNewMarker.setIcon({
        url: getMarkerIcon(newMarkerInData.assetType, newMarkerInData),
        scaledSize: new window.google.maps.Size(30, 30)
      });
      return; // Skip full refresh if we only updated the newMarker
    }

    // Clean up old markers
    markersRef.current.forEach(m => m.setMap(null));
    markersRef.current = [];

    if (clustererRef.current) {
      clustererRef.current.clearMarkers();
    }

    // Skip anything with missing/invalid coordinates to keep the map + clusterer from crashing.
    const newMarkers = (markers || []).map((m) => {
      if (!m) return null;

      const position = toLatLngLiteral(m.Latitude, m.Longitude);
      if (!position) {
        console.warn("Skipping marker with invalid coords", {
          GlobalID: m.GlobalID,
          assetType: m.assetType,
          Latitude: m.Latitude,
          Longitude: m.Longitude,
        });
        return null;
      }

      const marker = new window.google.maps.Marker({
        position,
        // If you want a custom icon, define it here.
        icon: {
          url: getMarkerIcon(m.assetType, m),
          scaledSize: new window.google.maps.Size(30, 30) // Adjust size as needed
        },
        title: createReadableTooltip(m),   // Create a readable tooltip
      });

      // If you want custom data:
      marker.myAssetType = m.assetType;
      marker.myId = m.id || m.GlobalID;
      marker.globalId = m.GlobalID; // Store GlobalID for finding markers

      // Example: attach an onClick:
      marker.addListener("click", () => {
        // If you want to do something like "EditMarker" callback, call it here:
        console.log("EditMarker clicked", addingPolyLineRef.current);
        if (!addingPolyLineRef.current) {
          setSelectedMarkerId(m.GlobalID);
          marker.setOptions({
            opacity: selectedMarkerId && m.GlobalID !== selectedMarkerId ? 0.6 : 1.0,
          });
          EditMarker({ markerData: m });
        } else {
          // Adding polyline - add this marker's point to the current polyline
          const markerLatLng = toLatLngLiteral(m.Latitude, m.Longitude);
          if (!markerLatLng) return;
          
          // const newLine = { lat, lng };
          // console.log("latLngArray2", addedLinesLatLngRef.current);
          var tempLines = addedLinesLatLngRef.current.map(line => {
            return { lat: line[0], lng: line[1] }
          })
          tempLines.push(markerLatLng)
          setLineCoordinates((prev) => [...prev, markerLatLng]);
          CallBackToParentForLine(tempLines)
          
          // Optionally, you can also add a visual indicator that this marker is part of the polyline
          marker.setOptions({
            opacity: 0.8, // Slightly transparent to show it's part of the polyline
            zIndex: 1000 // Bring to front
          });
        }
      });
      marker.setOptions({
        opacity: selectedMarkerId && m.GlobalID !== selectedMarkerId ? 0.6 : 1.0,
      });

      return marker;
    }).filter(Boolean);

    // Attach them to the map (or clusterer) in one pass
    if (clustererRef.current) {
      // If we have a clusterer:
      if (newMarkers.length > 0) {
        clustererRef.current.addMarkers(newMarkers);
      }
    } else {
      // Otherwise add to the map individually
      newMarkers.forEach((mk) => mk.setMap(map));
    }
    // store them in ref
    markersRef.current = newMarkers;
  }, [markers, map, selectedMarkerId, addingPolyLine, addedLinesLatLng, lineCoordinates]);

  // Handle live preview of marker color changes (e.g., when editing HydrantColor)
  useEffect(() => {
    if (!previewMarkerUpdate || !map) return;
    
    const { globalId, color, assetType } = previewMarkerUpdate;
    if (!globalId || assetType?.toLowerCase() !== 'hydrant') return;
    
    // Find the marker by GlobalID
    const marker = markersRef.current.find(m => m.globalId === globalId);
    
    if (marker) {
      // Update the marker icon with the new color
      marker.setIcon({
        url: getColoredHydrantSvg(color),
        scaledSize: new window.google.maps.Size(30, 30)
      });
    }
  }, [previewMarkerUpdate, map]);

  // Handle map type changes via our custom controls
  const handleMapTypeChange = (newMapType) => {
    if (map && newMapType !== currentMapType) {
      map.setMapTypeId(newMapType);
      setCurrentMapType(newMapType);
    }
  };

  // Handle editing marker state
  useEffect(() => {
    if (!map) return;
    setIsEditingMarker(editingData && editingData.GlobalID ? true : false);
  }, [map, editingData]);

  // Handle form and add marker logic
  useEffect(() => {
    if (!map) return;
    setIsEditingMarker(editingData && editingData.GlobalID ? true : false)
    if (showFormAndAddMarker && !isEditingMarker && !initialAdd) {
      // initialAddMarker()
      setInitialAdd(true)
      if (window.innerWidth < 767) {
        const relativNavbarHeight = (navbarHeight / window.innerHeight) * 100; // Convert to percentage
        setContainerStyle({
          position: "fixed",
          marginTop: `${relativNavbarHeight}vh`,
          width: "100vw",
          height: isMobile ? `${45 - relativNavbarHeight}vh` : `${50 - relativNavbarHeight}vh`,
        });
      }
    }
    if (!showFormAndAddMarker && !isEditingMarker && initialAdd) {
      const newMarker = markersRef.current.find(marker => marker.title === 'newMarker');
      if (newMarker) {
        newMarker.setMap(null);
        setInitialAdd(false);
      }
      if (window.innerWidth < 767) {
        const relativNavbarHeight = (navbarHeight / window.innerHeight) * 100; // Convert to percentage
        setContainerStyle({
          position: "fixed",
          marginTop: `${relativNavbarHeight}vh`,
          width: "100vw",
          height: `${100 - relativNavbarHeight}vh`,
        });
      }
    }
    if (addingPolyLine && !isEditingMarker) {
      const newMarker = markersRef.current.find(marker => marker.title === 'newMarker');
      if (newMarker) {
        newMarker.setMap(null);
        setInitialAdd(false);
      }
    }
  // move marker to new position. FInd the marker with the GlobalID of editingData and set its position to the new position
  if (movingMarker && editingData && editingData.GlobalID) {
    const newMarker = markersRef.current.find(marker => marker.myId === editingData.GlobalID);
    // set marker as draggable
    if (newMarker) {
      newMarker.setMap(map); // Ensure the marker is on the map
      newMarker.setDraggable(true); // Make the marker draggable
    }
    // add dragend event listener to the marker
    newMarker.addListener("dragend", (event) => {
      const newLat = event.latLng.lat();
      const newLng = event.latLng.lng();
      const newPosition = { lat: newLat, lng: newLng };
      // Update the existing marker's position in the markers array (don't create duplicate)
      const tempMarkers = markers.map(m => 
        m.GlobalID === editingData.GlobalID 
          ? { ...m, Latitude: newLat, Longitude: newLng }
          : m
      );
      // Call the callback to update the marker position in the parent component
      CallBackToParent(newPosition, tempMarkers);
      // set the marker position to the new Position
      newMarker.setPosition(newPosition);
    });
  }
  // remove dragend event listener from the marker when the user clicks on the map
  console.log('cancelMarker', cancelMarker)
  if (cancelMarker && editingData && editingData.GlobalID) {
    const newMarker = markersRef.current.find(marker => marker.title === editingData.GlobalID);
    if (newMarker) {
      newMarker.setDraggable(false); // Make the marker draggable\
      // remove the dragend event listener from the marker
      window.google.maps.event.clearListeners(newMarker, 'dragend');
    }
  } else if (cancelMarker) {
    console.log('got hrer', cancelMarker)
    setSelectedMarkerId(null);
    setSelectedLineId(null);
  }

  if (resetMarker && editingData && editingData.GlobalID) {
    const newMarker = markersRef.current.find(marker => marker.title === editingData.GlobalID);
    if (newMarker) {
      const originalPosition = { lat: editingData.Latitude, lng: editingData.Longitude };
      newMarker.setPosition(originalPosition);
    }
  }

  //clear lines
  if (clearLines) {
    setLineCoordinates([])
    clearLinesFunc(false)
  }
  let relativNavbarHeight = 0
    if (showFormAndAddMarker || addingMarker) {
      if (window.innerWidth < 767 && (showFormAndAddMarker || addingMarker)) {
        relativNavbarHeight = ((isMobile ? 68 : 73.6) / window.innerHeight) * 100;
        setContainerStyle({
          position: "fixed",
          marginTop: `${relativNavbarHeight}vh`,
          width: isMobile ? "100vw" : "97vw",
          height: isMobile ? `${45 - relativNavbarHeight}vh` : `${50 - relativNavbarHeight}vh`,
        });
      }
    } else if ((!showFormAndAddMarker || !addingMarker)) {
      relativNavbarHeight = (navbarHeight / window.innerHeight) * 100; // Convert to percentage
      setContainerStyle({
        position: "fixed",
        marginTop: `${relativNavbarHeight}vh`,
        width: "100vw",
        height: `${100 - relativNavbarHeight}vh`,
      });
    }
  }, [map, showFormAndAddMarker, editingData, initialAdd, addingPolyLine, isEditingMarker, cancelMarker, resetMarker, clearLines, addedLinesLatLng, addingMarker, movingMarker]);

  // useeffect to update mapCenter
  useEffect(() => {
    if (!map) return;
    if (map && mapCenter && mapCenter.length === 2
      && googleMapCenter[0] !== mapCenter[0] && googleMapCenter[1] !== mapCenter[1]) {
      const center = toLatLngLiteral(mapCenter[0], mapCenter[1]);
      if (center) map.setCenter(center);
      setGoogleMapCenter(mapCenter);
    } else if (map && mapCenter && mapCenter.length === 2) {
      setGoogleMapCenter(mapCenter);
    }

  }, [mapCenter]);

  // If you want to re-run cluster logic on some filter prop, you can do so in a separate effect
  // e.g. filter out certain marker types => marker.setMap(mapOrNull)

  // If the user clicks the map to add lines/markers
  const handleMapClick = useCallback((event) => {
    if (!map) return;
    
    // Prevent map interactions for read-only users
    if (!canWriteRef.current) {
      console.log("Map click ignored - read-only mode");
      return;
    }

    const lat = event.latLng.lat();
    const lng = event.latLng.lng();

    // Compute isEditingMarker from editingData directly (not from state) to avoid stale closure
    const currentIsEditingMarker = editingData && editingData.GlobalID ? true : false;
    // add marker to map
    if (addingMarker && !currentIsEditingMarker) {
      // Check if a marker with title "newMarker" already exists
      const existingMarker = markersRef.current.find(marker => marker.title === "newMarker");
      console.log('existingMarker', existingMarker)
      if (existingMarker) {
        // Update existing marker position
        existingMarker.setPosition({ lat, lng });
        existingMarker.setMap(map);
      } else {
        // Create a new marker if none exists
        const newMarker = new window.google.maps.Marker({
          position: { lat, lng },
          icon: {
            url: getMarkerIcon(addedAssetType),
            scaledSize: new window.google.maps.Size(30, 30)
          },
          title: "newMarker",
        });
        newMarker.setMap(map);
        markersRef.current.push(newMarker);
      }

      // Update marker data in parent component
      const newPosition = { lat, lng };
      const newMarkerData = {
        Latitude: lat,
        Longitude: lng,
        GlobalID: "newMarker",
        id: "newMarker",
        assetType: addedAssetType,
      };

      // Filter out any existing newMarker before adding the updated one
      const updatedMarkers = markers.filter(marker => 
        marker && marker.GlobalID !== "newMarker"
      );
      
      CallBackToParent(newPosition, [...updatedMarkers, newMarkerData]);
      setSelectedMarkerId(newMarkerData.GlobalID);
      // setIsEditingMarker(true);
      // setInitialAdd(true);

      // Reverse geocode the clicked position to auto-fill address fields
      if (onGeocodedAddress) {
        reverseGeocode(lat, lng).then((fields) => {
          if (fields) {
            onGeocodedAddress(fields);
          }
        });
      }
    }

    // If user is adding lines or points, call your line logic or callback
    if (addingPolyLine) {
      // e.g. update your parent with new line coords
      const newLine = { lat, lng };
      var tempLines = addedLinesLatLngRef.current.map(line => {
        return { lat: line[0], lng: line[1] }
      })
      tempLines.push(newLine)
      setLineCoordinates((prev) => [...prev, { lat, lng }]);
      CallBackToParentForLine(tempLines)

    } else {
      console.log("Map clicked at", lat, lng);
    }
  }, [map, addingPolyLine, addedLinesLatLng, lineCoordinates, addingMarker, addedAssetType, editingData, onGeocodedAddress]);

  // Render polylines for lines
  const renderPolylines = () => {
    if (!lines || !Array.isArray(lines)) return null;
    return lines.map((pathArray) => {
      const path = JSON.parse(pathArray.LineJSON);
      if (!path || !Array.isArray(path)) return null; // Ensure path is valid
      const polylinePath = path
        .map((point) => toLatLngLiteral(point?.[0], point?.[1]))
        .filter(Boolean);
      if (polylinePath.length < 2) return null;
      
      // Determine if this line is active (selected or no selection)
      const isActive = selectedLineId === pathArray.GlobalID || selectedLineId === null;
      
      // Get line color based on type and material (blue=water, green=sewer, red=lead)
      const material = pathArray.WaterLineMaterial || pathArray.SewerLineMaterial || null;
      const lineColor = getLineColor(pathArray.assetType, material, isActive);
      
      // Get arrow color based on pipe diameter
      const arrowColor = getArrowColorByDiameter(pathArray.LineDiameter);

      return <Polyline
        key={pathArray.GlobalID}
        path={polylinePath}
        onClick={() => {
          setSelectedLineId(pathArray.GlobalID);
          EditMarker({ markerData: pathArray })
        }}
        options={{
          strokeColor: lineColor,
          strokeOpacity: 1.0,
          strokeWeight: 3,
          icons: [
            {
              icon: {
                path: window.google?.maps.SymbolPath.FORWARD_CLOSED_ARROW,
                scale: 3,
                strokeColor: arrowColor,
                fillColor: arrowColor,
              },
              offset: "100%", // use "0%" or "50%" for different arrow positions
              repeat: "50px", // repeat along line
            },
          ],
        }}
      />
    });
  };

  const renderDrawingLine = () => {
    if (lineCoordinates && lineCoordinates.length !== 0) {
      const lineColor = addedAssetType === 'WaterLine' ? 'blue' : 'green'; // Blue for water lines, green for sewer lines
      return <Polyline
        path={lineCoordinates}
        options={{
          strokeColor: lineColor,
          strokeOpacity: 1.0,
          strokeWeight: 3,
          icons: [
            {
              icon: {
                path: window.google?.maps.SymbolPath.FORWARD_CLOSED_ARROW,
                scale: 3,
                strokeColor: lineColor,
                fillColor: lineColor,
              },
              offset: "100%", // use "0%" or "50%" for different arrow positions
              repeat: "50px", // repeat along line
            },
          ],
        }}
      />
    }
  }

  const initialAddMarker = async () => {
    const center = map.getCenter();
    const latLng = {
      lat: center.lat(),
      lng: center.lng()
    };
    // add marker to markers
    const addedMarker = {
      Latitude: latLng.lat,
      Longitude: latLng.lng,
      assetType: addedAssetType,
      GlobalID: "newMarker",
      id: "newMarker",
      opacity: 1
    }
    var tempMarkers = markers
    tempMarkers.push(addedMarker)

    CallBackToParent(latLng, tempMarkers)
    // Create a newMarker with the given latLng
    const newMarker = new window.google.maps.Marker({
      position: latLng,
      icon: {
        url: getMarkerIcon(addedAssetType),
        scaledSize: new window.google.maps.Size(30, 30) // Adjust size as needed
      },
      title: "newMarker",
    });

    newMarker.setMap(map);
    markersRef.current.push(newMarker); // Store the marker in the ref

  }

  // Helper for icon. This is optional – if you prefer a plain marker, skip
  function getMarkerIcon(assetType, markerData) {
    if (!assetType) return hydrantSVG;
    switch (assetType.toLowerCase()) {
      case "hydrant":
        // Use colored hydrant if Color is available (field is named 'Color' in data, not 'HydrantColor')
        const hydrantColor = markerData?.Color || markerData?.HydrantColor;
        if (hydrantColor) {
          return getColoredHydrantSvg(hydrantColor);
        }
        return hydrantSVG;
      case "manhole":
        return manholeSVG;
      case "trafficsign":
        return trafficSignSVG;
      case "valve":
        return valveSVG;
      case "watermeter":
        return waterMeterSVG;
      default:
        return hydrantSVG;
    }
  }

  if (!isLoaded) {
    return <div>Loading Map...</div>;
  }

  return (
    <div className="google-map-container" style={{ position: "relative", width: "100vw", height: "100vh" }}>
      {/* Left Sidebar Controls - Grouped circular buttons */}
      <div style={{
        position: "absolute",
        top: "10px",
        left: "10px",
        zIndex: 1000,
        display: "flex",
        flexDirection: "column",
        gap: "12px",
        alignItems: "flex-start"
      }}>
        {/* Map Type Toggle - Switch button */}
        <Tooltip title={currentMapType === "satellite" ? "Switch to Map View" : "Switch to Satellite View"} placement="right">
          <Box
            sx={{
              backgroundColor: "white",
              borderRadius: "24px",
              padding: "4px 8px",
              boxShadow: "0 2px 6px rgba(0,0,0,0.3)",
              display: "flex",
              alignItems: "center",
              gap: 1
            }}
          >
            <span style={{ fontSize: "11px", fontWeight: "500", color: currentMapType === "roadmap" ? "#1976d2" : "#666", minWidth: "28px" }}>
              Map
            </span>
            <Switch
              checked={currentMapType === "satellite"}
              onChange={() => handleMapTypeChange(currentMapType === "satellite" ? "roadmap" : "satellite")}
              size="small"
              sx={{
                '& .MuiSwitch-switchBase.Mui-checked': {
                  color: '#1976d2',
                },
                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                  backgroundColor: '#1976d2',
                },
              }}
            />
            <span style={{ fontSize: "11px", fontWeight: "500", color: currentMapType === "satellite" ? "#1976d2" : "#666", minWidth: "32px" }}>
              Sat
            </span>
          </Box>
        </Tooltip>

        {/* Camera + remove search highlights — horizontal row at top of stack */}
        {((canWrite && onQuickCaptureClick) || (hasHighlightedMarkers && onRemoveHighlights)) && (
          <div
            style={{
              display: "flex",
              flexDirection: "row",
              flexWrap: "wrap",
              gap: "12px",
              alignItems: "center",
            }}
          >
            {canWrite && onQuickCaptureClick && (
              <Tooltip title="Quick Capture" placement="right">
                <Fab
                  color="primary"
                  aria-label="Quick Capture"
                  onClick={onQuickCaptureClick}
                  sx={{
                    width: 48,
                    height: 48,
                    boxShadow: "0 2px 6px rgba(0,0,0,0.3)",
                  }}
                >
                  <CameraAltOutlinedIcon sx={{ fontSize: 24 }} />
                </Fab>
              </Tooltip>
            )}
            {hasHighlightedMarkers && onRemoveHighlights && (
              <Tooltip title="Remove highlights" placement="right">
                <button
                  type="button"
                  aria-label="Remove search highlights"
                  onClick={onRemoveHighlights}
                  style={{
                    position: "relative",
                    width: 48,
                    height: 48,
                    borderRadius: "50%",
                    border: "1px solid red",
                    backgroundColor: "white",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    padding: 0,
                    outline: "none",
                    boxShadow: "0 2px 6px rgba(0,0,0,0.3)",
                    transition: "background-color 0.2s ease",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = "#f5f5f5";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = "white";
                  }}
                >
                  <img
                    src={searchRemoveHighlight}
                    alt=""
                    style={{ width: 22, height: 22 }}
                  />
                  <span
                    style={{
                      position: "absolute",
                      bottom: 4,
                      right: 2,
                      lineHeight: 0,
                      pointerEvents: "none",
                    }}
                  >
                    <CancelIcon sx={{ fill: "red", fontSize: 14 }} />
                  </span>
                </button>
              </Tooltip>
            )}
          </div>
        )}

        {/* Location Button - Center map on user location */}
        {onCenterMap && (
          <Tooltip title="Move to my location" placement="right">
            <button
              onClick={onCenterMap}
              style={{
                width: 48,
                height: 48,
                borderRadius: "50%",
                border: "1px solid red",
                backgroundColor: "white",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                padding: "3px",
                outline: "none",
                boxShadow: "0 2px 6px rgba(0,0,0,0.3)",
                transition: "background-color 0.2s ease"
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = "#f5f5f5";
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = "white";
              }}
            >
              {isLoadingLocation ? (
                <CircularProgress size={20} />
              ) : (
                <LocationOnIcon style={{ color: "red", fontSize: 24 }} />
              )}
            </button>
          </Tooltip>
        )}

        {/* Refresh Button */}
        {onRefreshData && (
          <Tooltip title="Refresh assets" placement="right">
            <button
              onClick={onRefreshData}
              style={{
                width: 48,
                height: 48,
                borderRadius: "50%",
                border: "1px solid rgb(94, 0, 182)",
                backgroundColor: "white",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                padding: "3px",
                outline: "none",
                boxShadow: "0 2px 6px rgba(0,0,0,0.3)",
                transition: "background-color 0.2s ease"
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = "#f5f5f5";
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = "white";
              }}
            >
              {refreshing ? (
                <CircularProgress size={20} />
              ) : (
                <RefreshIcon style={{ fill: "rgb(94, 0, 182)", fontSize: 24 }} />
              )}
            </button>
          </Tooltip>
        )}
      </div>
      
      <GoogleMap
        mapContainerStyle={containerStyle}
        onLoad={onLoad}
        onUnmount={onUnmount}
        onClick={handleMapClick}
        options={{
          gestureHandling: "greedy",
          zoomControl: true,
          disableDefaultUI: false,
          mapTypeControl: false, // Disable Google's map type controls
          mapTypeId: currentMapType, // Use our controlled map type
          fullscreenControl: false, // Hide the fullscreen button
          // Hide tilt / 2D–3D / rotate UI (bottom-left on satellite & vector maps)
          rotateControl: false,
          cameraControl: false,
          // Vector maps only: disable tilt gesture + control (no effect on pure raster)
          tiltInteractionEnabled: false,
        }}
        // If you want a default center
        // center={
        //   mapCenter && mapCenter.length === 2
        //     ? { lat: mapCenter[0], lng: mapCenter[1] }
        //     : { lat: 36.144626, lng: -97.291164 }
        // }
        zoom={zoom || 10}
      >
        {/* Polylines if you still want to display lines */}
        {renderPolylines()}
        {renderDrawingLine()}

      </GoogleMap>

      {/* Line Legend for type and diameter color coding */}
      <LineLegend />

    </div>
  );
}

export default React.memo(CustomMap);

